import useRoutingContext from '@context/routing/useRoutingContext';
import { SCHEDULING_PAGE } from '@app/routes/urls';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FiPauseCircle } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import usePlanningSession from '../hooks/usePlanningSession';

/**
 * The header's reminder that a planning session is open, and the way back to it.
 *
 * The bar lives on the scheduling page now, but the session does not: assignments made from the
 * visits calendar are snoozed by the same session. So off that page this is the ONLY sign that
 * caregivers are deliberately being kept in the dark, which is why it carries the count rather
 * than being a bare icon — a planner has to be able to tell "planning is open, 3 visits changed"
 * without navigating anywhere.
 *
 * It hides on the scheduling page itself. The bar is already there with the same number plus the
 * controls, and two live counts on one screen invites the question of which one is right.
 */
const PlanningSessionIndicator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { RouteConstruct } = useRoutingContext();
  const { session } = usePlanningSession();

  // Resolved by the routing system, not spelled out here: `full_path` is assembled from the
  // route tree (`addFullPathToRoutes`), so a hardcoded string would be a second answer to
  // "where is scheduling" that nothing keeps in step with the first.
  const schedulingPath = RouteConstruct[SCHEDULING_PAGE]?.full_path ?? '';

  if (!session) return null;
  if (schedulingPath && location.pathname.startsWith(schedulingPath)) return null;

  return (
    <Tooltip title={t('planning-session-indicator-tooltip', { count: session.pendingChangeCount })}>
      <button
        type="button"
        onClick={() => navigate(schedulingPath)}
        aria-label={t('planning-session-indicator-aria', { count: session.pendingChangeCount })}
        data-testid="planning-session-indicator"
        className="relative inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 text-indigo-900 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-gray-100"
      >
        <FiPauseCircle className="h-5 w-5 shrink-0" />
        <span className="hidden text-sm font-semibold sm:inline">
          {t('planning-session-bar-held', { count: session.pendingChangeCount })}
        </span>
        {/* The count survives the narrow-screen collapse: the label is what can be dropped,
            the number is the thing this button exists to carry. */}
        <span className="text-sm font-semibold tabular-nums sm:hidden">{session.pendingChangeCount}</span>
      </button>
    </Tooltip>
  );
};

export default PlanningSessionIndicator;
